import {
  getText
} from "./chunk.3GLWK2TQ.js";
import {
  LocalizeController
} from "./chunk.ZPJRWEFM.js";
import {
  WebAwesomeElement,
  e,
  n,
  r,
  t
} from "./chunk.LVUMIIKQ.js";
import {
  ke
} from "./chunk.S3625EU6.js";
import {
  __decorateClass
} from "./chunk.SZE6ONST.js";

// src/components/option/option.css
var option_default = ":host {\n  --background-color-current: var(--wa-color-brand-fill-loud);\n  --background-color-hover: var(--wa-color-neutral-fill-normal);\n  --text-color-current: var(--wa-color-brand-on-loud);\n  --text-color-hover: var(--wa-color-neutral-on-normal);\n\n  display: block;\n  color: var(--wa-color-text-normal);\n  -webkit-user-select: none;\n  user-select: none;\n\n  position: relative;\n  display: flex;\n  align-items: center;\n  font: inherit;\n  padding: var(--wa-space-xs) var(--wa-space-m) var(--wa-space-xs) var(--wa-space-2xs);\n  line-height: var(--wa-line-height-condensed);\n  transition: fill var(--wa-transition-normal) var(--wa-transition-easing);\n  cursor: pointer;\n}\n\n:host(:focus) {\n  outline: none;\n}\n\n:host(:not([disabled], :state(current)):is(:state(hover), :hover)) {\n  background-color: var(--background-color-hover);\n  color: var(--text-color-hover);\n}\n\n:host(:state(current)),\n:host([disabled]:state(current)) {\n  background-color: var(--background-color-current);\n  color: var(--text-color-current);\n  opacity: 1;\n}\n\n:host([disabled]) {\n  outline: none;\n  opacity: 0.5;\n  cursor: not-allowed;\n}\n\n.label {\n  flex: 1 1 auto;\n  display: inline-block;\n}\n\n.check {\n  flex: 0 0 auto;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  font-size: 0.875em;\n  visibility: hidden;\n  width: var(--wa-space-xl);\n}\n\n:host(:state(selected)) .check {\n  visibility: visible;\n}\n\n.prefix,\n.suffix {\n  flex: 0 0 auto;\n  display: flex;\n  align-items: center;\n}\n\n.prefix::slotted(*) {\n  margin-inline-end: var(--wa-space-xs);\n}\n\n.suffix::slotted(*) {\n  margin-inline-start: var(--wa-space-xs);\n}\n\n@media (forced-colors: active) {\n  :host(:hover:not([aria-disabled='true'])) {\n    outline: dashed 1px SelectedItem;\n    outline-offset: -1px;\n  }\n}\n";

// src/components/option/option.ts
var WaOption = class extends WebAwesomeElement {
  constructor() {
    super(...arguments);
    // @ts-expect-error - Controller is currently unused
    this.localize = new LocalizeController(this);
    this.isInitialized = false;
    this.current = false;
    this.selected = false;
    this.value = "";
    this.disabled = false;
    this._label = "";
    this.defaultLabel = "";
    this.handleHover = (event) => {
      if (event.type === "mouseenter") {
        this.toggleCustomState("hover", true);
      } else if (event.type === "mouseleave") {
        this.toggleCustomState("hover", false);
      }
    };
  }
  set label(value) {
    const oldValue = this._label;
    this._label = value || "";
    if (this._label !== oldValue) {
      this.requestUpdate("label", oldValue);
    }
  }
  get label() {
    if (this._label) {
      return this._label;
    }
    if (!this.defaultLabel) {
      this.updateDefaultLabel();
    }
    return this.defaultLabel;
  }
  connectedCallback() {
    super.connectedCallback();
    this.setAttribute("role", "option");
    this.setAttribute("aria-selected", "false");
    this.addEventListener("mouseenter", this.handleHover);
    this.addEventListener("mouseleave", this.handleHover);
    this.updateDefaultLabel();
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("mouseenter", this.handleHover);
    this.removeEventListener("mouseleave", this.handleHover);
  }
  handleDefaultSlotChange() {
    if (customElements.get("wa-select")) {
      this.closest("wa-select")?.selectionChanged();
    }
    this.updateDefaultLabel();
    if (this.isInitialized) {
      customElements.whenDefined("wa-select").then(() => {
        const controller = this.closest("wa-select");
        if (controller) {
          controller.handleDefaultSlotChange();
        }
      });
    } else {
      this.isInitialized = true;
    }
  }
  updated(changedProperties) {
    super.updated(changedProperties);
    if (changedProperties.has("disabled")) {
      this.setAttribute("aria-disabled", this.disabled ? "true" : "false");
    }
    if (changedProperties.has("selected")) {
      this.setAttribute("aria-selected", this.selected ? "true" : "false");
      this.toggleCustomState("selected", this.selected);
    }
    if (changedProperties.has("value")) {
      if (typeof this.value !== "string") {
        this.value = String(this.value);
      }
      if (this.value.includes(" ")) {
        console.error(`Option values cannot include a space. All spaces have been replaced with underscores.`, this);
        this.value = this.value.replace(/ /g, "_");
      }
      this.handleDefaultSlotChange();
    }
    if (changedProperties.has("current")) {
      this.toggleCustomState("current", this.current);
    }
  }
  updateDefaultLabel() {
    let oldValue = this.defaultLabel;
    this.defaultLabel = getText(this).trim();
    let changed = this.defaultLabel !== oldValue;
    if (!this._label && changed) {
      this.requestUpdate("label", oldValue);
    }
    return changed;
  }
  render() {
    return ke`
      <wa-icon
        part="checked-icon"
        class="check"
        name="check"
        library="system"
        variant="solid"
        aria-hidden="true"
      ></wa-icon>
      <slot part="prefix" name="prefix" class="prefix"></slot>
      <slot part="label" class="label" @slotchange=${this.handleDefaultSlotChange}></slot>
      <slot part="suffix" name="suffix" class="suffix"></slot>
    `;
  }
};
WaOption.shadowStyle = option_default;
__decorateClass([
  e(".label")
], WaOption.prototype, "defaultSlot", 2);
__decorateClass([
  r()
], WaOption.prototype, "current", 2);
__decorateClass([
  r()
], WaOption.prototype, "selected", 2);
__decorateClass([
  n({ reflect: true })
], WaOption.prototype, "value", 2);
__decorateClass([
  n({ type: Boolean, reflect: true })
], WaOption.prototype, "disabled", 2);
__decorateClass([
  n()
], WaOption.prototype, "label", 1);
__decorateClass([
  r()
], WaOption.prototype, "defaultLabel", 2);
WaOption = __decorateClass([
  t("wa-option")
], WaOption);

export {
  WaOption
};
